import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api from "../services/api";

const today = () => new Date().toISOString().slice(0, 10);

const WorktimeDailyAdmin = () => {
  const navigate = useNavigate();
  const [date, setDate] = useState(today());
  const [dept, setDept] = useState("ALL");
  const [departments, setDepartments] = useState([]);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [editNote, setEditNote] = useState({});
  const [editTimes, setEditTimes] = useState({});
  const [savingId, setSavingId] = useState(null);

  const fetchDepts = useCallback(async () => {
    try {
      const res = await api.get("/departments/");
      const list = Array.isArray(res.data) ? res.data : [];
      setDepartments(list.filter((d) => d.departmentId?.endsWith("00")));
    } catch (e) {
      console.error(e);
    }
  }, []);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setMessage("");
    try {
      const params = { date };
      if (dept && dept !== "ALL") params.departmentName = dept;
      const res = await api.get("/api/worktime/admin/daily/", { params });
      setRows(Array.isArray(res.data) ? res.data : (res.data?.results || []));
    } catch (e) {
      setMessage("データの取得に失敗しました。");
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [date, dept]);

  useEffect(() => {
    fetchDepts();
  }, [fetchDepts]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  /** 管理メモはオブジェクト or 文字列で来うるので、表示・編集用の文字列に正規化する */
  const noteToText = (note) => {
    if (note == null) return "";
    if (typeof note === "string") return note;
    if (typeof note === "object") return note.freeNote ?? note.displayMessage ?? "";
    return String(note);
  };

  const handleSaveRow = async (row) => {
    const mid = row.memberId;
    setSavingId(mid);
    try {
      const origStart = row.startTime || "";
      const origEnd = row.endTime || "";
      const t = editTimes[mid] || {};
      const curStart = t.start !== undefined ? t.start : origStart;
      const curEnd = t.end !== undefined ? t.end : origEnd;

      // 実打刻: 変更があった時刻だけ送る（変更していない打刻を「管理者修正」扱いにしないため）。
      // レコードが無ければサーバ側で新規作成される（打刻忘れの事後登録）。
      const recordPayload = { memberId: mid, date };
      if (curStart !== origStart && curStart) recordPayload.start_time = curStart;
      if (curEnd !== origEnd && curEnd) recordPayload.end_time = curEnd;
      if (recordPayload.start_time || recordPayload.end_time) {
        await api.post("/api/worktime/admin/record/", recordPayload);
      }

      // 管理メモ: 変更があれば保存（既存機能）
      const origNote = noteToText(row.adminNote);
      const freeNote = editNote[mid] ?? origNote;
      if (freeNote !== origNote) {
        await api.post("/api/worktime/admin/note/", {
          memberId: mid,
          date,
          free_note: freeNote,
        });
      }

      // 保存後は最新値（再計算された状態・計）を取り直し、編集中バッファをクリア
      setEditTimes((prev) => {
        const n = { ...prev };
        delete n[mid];
        return n;
      });
      setEditNote((prev) => {
        const n = { ...prev };
        delete n[mid];
        return n;
      });
      await fetchData();
    } catch (e) {
      alert(e.response?.data?.message || "保存に失敗しました。");
    } finally {
      setSavingId(null);
    }
  };

  const statusLabel = (row) => {
    const kind = row.scheduleDayKind || row.scheduleKind;
    if (kind === "holiday") return <span style={{ color: "#6b7280" }}>休日</span>;
    if (kind === "paid") return <span style={{ color: "#16a34a" }}>有給</span>;
    const inT = row.startTime || row.clockIn;
    const outT = row.endTime || row.clockOut;
    if (!inT) return <span style={{ color: "#dc2626", fontWeight: 700 }}>打刻なし</span>;
    if (!outT) return <span style={{ color: "#d97706", fontWeight: 700 }}>退社未打刻</span>;
    return <span style={{ color: "#374151" }}>完了</span>;
  };

  return (
    <div style={{ padding: "20px 16px 60px", maxWidth: 960, margin: "0 auto", fontFamily: "Hiragino Kaku Gothic ProN, Meiryo, sans-serif" }}>
      <div style={{ display: "flex", alignItems: "center", marginBottom: 16, gap: 12 }}>
        <button type="button" onClick={() => navigate(-1)} style={{ background: "none", border: "none", fontSize: "1.2rem", cursor: "pointer", color: "#374151" }}>←</button>
        <h1 style={{ textAlign: "center", margin: 0, fontSize: "1.3rem", fontWeight: 700, color: "#1e3a5f" }}>日別勤怠・タイムカード</h1>
      </div>

      <div style={{ display: "flex", gap: 12, marginBottom: 16, flexWrap: "wrap", alignItems: "center" }}>
        <input
          type="date"
          value={date}
          onChange={(e) => setDate(e.target.value)}
          style={{ padding: "7px 10px", border: "1px solid #d1d5db", borderRadius: 6, fontSize: "0.9rem" }}
        />
        <select
          value={dept}
          onChange={(e) => setDept(e.target.value)}
          style={{ padding: "7px 10px", border: "1px solid #d1d5db", borderRadius: 6, fontSize: "0.9rem", background: "#fff" }}
        >
          <option value="ALL">全部署</option>
          {departments.map((d) => (
            <option key={d.departmentId} value={d.departmentName}>{d.departmentName}</option>
          ))}
        </select>
        <button
          type="button"
          onClick={fetchData}
          style={{ padding: "7px 16px", background: "#2563eb", color: "#fff", border: "none", borderRadius: 6, fontSize: "0.9rem", fontWeight: 600, cursor: "pointer" }}
        >
          検索
        </button>
      </div>

      {message && <p style={{ color: "#dc2626", fontSize: "0.88rem" }}>{message}</p>}

      <p style={{ fontSize: "0.8rem", color: "#6b7280", margin: "0 0 10px" }}>
        出社・退社の時刻を直接修正できます（打刻忘れの事後入力も可）。修正して「保存」を押してください。
        <span style={{ color: "#dc2626", fontWeight: 700 }}>※</span> は管理者修正済みを表します。
      </p>

      {loading ? (
        <p style={{ textAlign: "center", color: "#6b7280" }}>読み込み中...</p>
      ) : rows.length === 0 ? (
        <p style={{ textAlign: "center", color: "#6b7280", marginTop: 30 }}>対象データがありません。</p>
      ) : (
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.82rem", background: "#fff", borderRadius: 8, boxShadow: "0 2px 8px rgba(0,0,0,0.07)" }}>
            <thead>
              <tr style={{ background: "#f1f5f9", color: "#374151" }}>
                {["部署", "社員番号", "氏名", "シフト", "出社", "退社", "状態", "管理メモ", ""].map((h) => (
                  <th key={h} style={{ padding: "8px 10px", textAlign: "left", borderBottom: "1px solid #e5e7eb", whiteSpace: "nowrap" }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => {
                const mid = row.memberId;
                const curStart = editTimes[mid]?.start !== undefined ? editTimes[mid].start : (row.startTime || "");
                const curEnd = editTimes[mid]?.end !== undefined ? editTimes[mid].end : (row.endTime || "");
                const memoText = editNote[mid] ?? noteToText(row.adminNote);
                return (
                  <tr key={mid} style={{ borderBottom: "1px solid #f3f4f6" }}>
                    <td style={{ padding: "7px 10px", color: "#6b7280" }}>{row.departmentName || ""}</td>
                    <td style={{ padding: "7px 10px" }}>{mid || ""}</td>
                    <td style={{ padding: "7px 10px", fontWeight: 600 }}>{row.name}</td>
                    <td style={{ padding: "7px 10px", color: "#6b7280" }}>
                      {row.scheduleStart && row.scheduleEnd ? `${row.scheduleStart}〜${row.scheduleEnd}` : "—"}
                    </td>
                    <td style={{ padding: "7px 10px", whiteSpace: "nowrap" }}>
                      <input
                        type="time"
                        value={curStart}
                        onChange={(e) => setEditTimes((prev) => ({ ...prev, [mid]: { ...prev[mid], start: e.target.value } }))}
                        style={{ padding: "3px 6px", border: "1px solid #d1d5db", borderRadius: 5, fontSize: "0.8rem" }}
                      />
                      {row.adminCorrectedStart && (
                        <span title={`管理者修正（元: ${row.originalStartTime || "—"}）`} style={{ color: "#dc2626", fontWeight: 700, marginLeft: 4 }}>※</span>
                      )}
                    </td>
                    <td style={{ padding: "7px 10px", whiteSpace: "nowrap" }}>
                      <input
                        type="time"
                        value={curEnd}
                        onChange={(e) => setEditTimes((prev) => ({ ...prev, [mid]: { ...prev[mid], end: e.target.value } }))}
                        style={{ padding: "3px 6px", border: "1px solid #d1d5db", borderRadius: 5, fontSize: "0.8rem" }}
                      />
                      {row.adminCorrectedEnd && (
                        <span title={`管理者修正（元: ${row.originalEndTime || "—"}）`} style={{ color: "#dc2626", fontWeight: 700, marginLeft: 4 }}>※</span>
                      )}
                    </td>
                    <td style={{ padding: "7px 10px" }}>{statusLabel(row)}</td>
                    <td style={{ padding: "7px 10px", minWidth: 120 }}>
                      <input
                        type="text"
                        value={memoText}
                        onChange={(e) => setEditNote((prev) => ({ ...prev, [mid]: e.target.value }))}
                        placeholder="メモを入力"
                        style={{ padding: "4px 8px", border: "1px solid #d1d5db", borderRadius: 5, fontSize: "0.8rem", width: "100%", boxSizing: "border-box" }}
                      />
                    </td>
                    <td style={{ padding: "7px 10px" }}>
                      <button
                        type="button"
                        onClick={() => handleSaveRow(row)}
                        disabled={savingId === mid}
                        style={{ padding: "4px 10px", background: "#16a34a", color: "#fff", border: "none", borderRadius: 5, fontSize: "0.78rem", cursor: "pointer", whiteSpace: "nowrap" }}
                      >
                        {savingId === mid ? "保存中" : "保存"}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default WorktimeDailyAdmin;
